export enum TripType {
  Sailing = 'Sailing',
  Berthing = 'Berthing',
  Repositioning = 'Repositioning',
  Shifting = 'Shifting',
  Resting = 'Resting',
  Uncertain = 'Uncertain job',
}

export enum ActivityType {
  Reposition = 'Reposition',
  Resting = 'Resting',
  Mobilization = 'Mobilization',
  Waiting = 'Waiting',
  Berthing = 'Berthing',
  Shifting = 'Shifting',
  Sailing = 'Sailing',
  Demobilization = 'Demobilization',
  Uncertain = 'Uncertain',
  Redeployment = 'Redeployment',
  SignalLost = 'SignalLost',
  Loading = 'Loading',
}

export enum SpeedType {
  SpeedThroughWater = 'SpeedThroughWater',
  SpeedOverGround = 'SpeedOverGround',
}

export type TimestampKey = number;
export type MmsiKey = string;

export interface Coordinates {
  lat: number;
  lon: number;
}

export interface Location {
  coordinates: Coordinates;
}

export interface TimestampModel {
  valueInMilliseconds: number;
}

export interface ITimestamp extends TimestampModel {
  toMilliseconds: () => number;
  toSeconds: () => number;
  toMinutes: () => number;
  toHours: () => number;
  toLocationDateTimeOffset: (timezone: string) => DateTimeOffset;
  toBrowserDateTimeOffset: () => DateTimeOffset;
  toISOString: () => string;
  getClockInformation: (timezone: string) => ClockData;
  add: (timestamp: ITimestamp) => ITimestamp;
  subtract: (timestamp: ITimestamp) => ITimestamp;
  multiply: (factor: number) => ITimestamp;
  isValid: () => boolean;
  toDate: () => Date;
}

export interface ITimer {
  start: () => void;
  stop: () => void;
  reset: () => void;
  restart: () => void;
  elapsed: ITimestamp;
}
export interface Range<T> {
  from: T;
  to: T;
}

export interface TimestampModelRange extends Range<TimestampModel> {}

export interface TimestampRange extends Range<ITimestamp> {}

export interface DateRange extends Range<Date> {}

export interface NumberRange extends Range<number> {}

export interface Speed {
  value: number;
  type: SpeedType;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export enum CONFIG_CODES {
  REACT_APP_MAPBOX_STYLE,
  REACT_APP_MAPBOX_SATELLITE_STYLE,
  REACT_APP_MAPBOX_NAUTICAL_STYLE,
  REACT_APP_API_HOST,
  REACT_APP_AUTH_CLIENT_ID,
  REACT_APP_AUTH_AUTHORITY,
  REACT_APP_AUTH_REDIRECT_URI,
  DISABLE_ESLINT_PLUGIN,
  ESLINT_NO_DEV_ERRORS,
  REACT_APP_SCRUBBER,
  MAX_ARROW_DISTANCE_FROM_POINT_IN_PIXELS,
}

export enum Page {
  Playback = 'Playback',
  Liveview = 'Liveview',
  Unknown = 'Unknown',
}

export enum Feature {
  InformationPanel = 'InformationPanel',
  Map = 'Map',
  FilterPanel = 'FilterPanel',
  Browser = 'Browser',
  Timeline = 'Timeline',
  TripsHistoryPanel = 'TripsHistoryPanel',
  LivePanel = 'LivePanel',
  GlobalSearch = 'GlobalSearch',
  PickTime = 'PickTime',
}

export enum EventType {
  UserAction = 'UserAction',
  AppEvent = 'AppEvent',
}

export enum Event {
  NoTripsFound = 'NoTripsFound',
  FilteredTripType = 'FilteredTripType',
  FilteredDate = 'FilteredDate',
  FilteredCompetitors = 'FilteredCompetitors',
  FiltersReset = 'FiltersReset',
  Exception = 'Exception',
  WrongInformation = 'WrongInformation',
  PanMap = 'PanMap',
  ZoomMap = 'ZoomMap',
  VesselHovered = 'VesselHovered',
  VesselTargetted = 'VesselTargetted',
  VesselUntargeted = 'VesselUntargeted',
  PlaybackStarted = 'PlaybackStarted',
  PlaybackPaused = 'PlaybackPaused',
  TimelineScrubbed = 'TimelineScrubbed',
  TimelineSpeedChanged = 'TimelineSpeedChanged',
  Entered3dView = 'Entered3dView',
  Exited3dView = 'Exited3dView',
  NPSSurvey = 'NPSSurvey',
  TimelineMarkerHovered = 'TimelineMarkerHovered',
  ShareLink = 'ShareLink',
  CopyLink = 'CopyLink',
  VesselSearchOpened = 'VesselSearchOpened',
  VesselSearchClosed = 'VesselSearchClosed',
  VesselSearchGotResults = 'VesselSearchGotResults',
  VesselSearchGotNoResults = 'VesselSearchGotNoResults',
  VesselFoundAndSelected = 'VesselFoundAndSelected',
  VesselTracksEnabled = 'VesselTracksEnabled',
  VesselTracksDisabled = 'VesselTracksDisabled',
  EnableVesselNames = 'EnableVesselNames',
  DisableVesselNames = 'DisableVesselNames',
  VesselTrackHovered = 'VesselTrackHovered',
  ActivityMarkerHovered = 'ActivityMarkerHovered',
  PickTimeOpened = 'PickTimeOpened',
  PickTimeClosed = 'PickTimeClosed',
  RedirectedToPlaybackFromPickTime = 'RedirectedToPlaybackFromPickTime',
  OperationSelected = 'OperationSelected',
  TripsHistoryPanelOpened = 'TripsHistoryPanelOpened',
  TripsHistoryPanelClosed = 'TripsHistoryPanelClosed',
  TripsPanelTripHovered = 'TripsPanelTripHovered',
  TripsPanelTripClicked = 'TripsPanelTripClicked',
  TripsPanelScrolledToEnd = 'TripsPanelScrolledToEnd',
  DisableQuiverPlot = 'DisableQuiverPlot',
  EnableQuiverPlot = 'EnableQuiverPlot',
  TidalDiamondHovered = 'TidalDiamondHovered',
  TidalArrowClicked = 'TidalArrowClicked',
  DisableNauticalMap = 'DisableNauticalMap',
  EnableNauticalMap = 'EnableNauticalMap',
  LiveViewButtonClicked = 'LiveViewButtonClicked',
  JumpBackButtonClicked = 'JumpBackButtonClicked',
}

export interface EventFilter {
  startDate: string;
  endDate: string;
  vesselName: string;
  port: string;
  tripType: string[];
  jobType: string;
}

export enum FilteredVesselEvent {
  KeyBoardEnter,
  MouseSelection,
  ClearTextInput,
}

export interface AnalyticsEvent {
  name: string;
  parent: Feature | Page;
  page: Page;
  [key: string]: any;
}

export enum NPSSurveyEvent {
  Close,
  ClickNumberAndClose,
  Submit,
}

export enum VesselTargetSource {
  ClickOnVessel = 'ClickOnVessel',
  ClickOnRecenterButton = 'ClickOnRecenterButton',
}

export enum ToasterVariant {
  Success = 'customSuccess',
  Warning = 'customWarning',
  Error = 'customError',
  Loading = 'customLoading',
}

export enum ShareLinkOperation {
  OpenLink = 'OpenLink',
  CloseLink = 'CloseLink',
}

export enum CopyLinkOperation {
  CopyDefaultUrl = 'CopyDefaultUrl',
  CopyTimeSpecificUrl = 'CopyTimeSpecificUrl',
}

export interface NavigationParams {
  port?: string;
  timestamp?: ITimestamp;
  mmsi?: string;
}

export interface Vessel {
  mmsi: string;
  type: string;
  name: string;
}

export interface Vessels {
  vessels: Vessel[];
}

export interface RecentPosition {
  mmsi: number;
  timestamp: TimestampModel;
  operation: string;
}

export enum JobTypes {
  all,
  svitzerTrip,
  competitorTrip,
}

export enum EcoZone {
  good = 'good',
  neutral = 'neutral',
  bad = 'bad',
}

export enum Source {
  PortMonitor = 'PortMonitor',
  Helm = 'Helm',
  Matched = 'Matched',
}

export enum TripsPanelState {
  Open = 'Open',
  Closed = 'Closed',
  Reopen = 'Reopen',
}

export interface JobActivity {
  activityId: string;
  activity: string;
  timestampStart: number;
  timestampEnd: number;
  fuelEfficiency: number;
  speedEfficiency: number;
}

export interface Job {
  jobId: string;
  timestampStart: number;
  timestampEnd: number;
  speedEfficiency: number;
  fuelEfficiency: number;
  totalFuelConsumption: number;
  totalFuelConsumptionExtrapolated: number;
  vesselMmsi: number;
  vesselImo: number;
  vesselName: string;
  activities: JobActivity[];
  source: Source;
}

export interface Trip {
  id: string;
  port: string;
  tripId: string;
  timestampStart: number;
  timestampEnd: number;
  fuelEfficiency: number;
  speedEfficiency: number;
  totalFuelConsumption: number;
  totalFuelConsumptionExtrapolated: number;
  type: string;
  assistedVesselMmsi: number;
  assistedVesselName: string;
  jobs: Job[];
  source: Source;
  customerName: string;
}

export interface TripJobPair {
  trip: Trip;
  job: Job;
}

export interface TripsPanelViewModel {
  trips: Trip[];
  paging: Paging;
}

export interface TripGroup {
  date: string;
  trips: Trip[];
}

export interface Paging {
  total: number;
  page: number;
  size: number;
  hasNextPage: boolean;
}

export interface Filters {
  port: string | null;
  startDate: string | null;
  endDate: string | null;
  vesselName: string | null;
  tripType: string[] | null;
  jobType: JobTypes | null;
}
export interface TripsData {
  trips: Trip[];
  isLoading: boolean;
}

export interface TripsEcoRatingViewModel {
  ecoRating: number;
  estimatedCo2Surplus: number;
  progress: number;
  dailyRatings: DailyEcoRating[];
}

export interface DailyEcoRating {
  day: string;
  ecoRating?: number;
  estimatedCo2Surplus?: number;
  progress?: number;
}

export interface TripsPanelStateManager {
  open: () => void;
  close: () => void;
  toggle: () => void;
  closeToReopen: () => void;
}

export interface DateTimeOffset {
  readonly timeString: string;
  readonly dateString: string;
  readonly offset: ITimestamp;
  readonly format: (format: string) => string;
}

export enum InformationPanelAction {
  Submit,
  Cancel,
}

export interface PlaybackViewModel {
  timestampRange: TimestampModelRange;
  vesselData: Record<MmsiKey, VesselViewModel>;
  vesselPositions: Record<TimestampKey, VesselPosition[]>;
  scrubberData: Record<MmsiKey, ScrubberViewModel>;
}

export interface LiveDataViewModel {
  vesselData: Record<MmsiKey, VesselViewModel>;
  vesselPositions: VesselPosition[];
}

export interface ScrubberViewModel {
  activities: ActivityViewModel[];
  helmEvents: HelmEvent[];
}

export interface BaseActivity {
  activityId: string;
  timestampRange: TimestampModelRange;
  type: ActivityType;
  isSpeedLimited: boolean;
}

export interface ActivityViewModel extends BaseActivity {
  speedChartData: SpeedChartEntry[];
  isTugActivity: boolean;
}

export interface HelmEvent {
  id: string;
  mmsi: string;
  type: HelmEventType;
  timestamp: TimestampModel;
  operation: string;
  assistedVesselName: string;
}

export interface HelmEventMarkerData extends Omit<HelmEvent, 'operation'> {
  offset: number;
}

export enum HelmEventType {
  RequestedTime = 'RequestedTime',
  Mobilization = 'Mobilization',
  Demobilisation = 'Demobilization',
  Sailing = 'Sailing',
  Berthing = 'Berthing',
  Waiting = 'Waiting',
  Shifting = 'Shifting',
  Escorting = 'Escorting',
  Working = 'Working',
}

export interface VesselViewModel {
  mmsi: string;
  imo: string;
  name: string;
  description: string;
  type: string;
  dimensions: VesselDimensions;
}

export interface VesselPosition {
  mmsi: string;
  status?: number;
  location: Location;
  timestamp: number;
  heading: number;
  speed: Speed;
  tidalSpeed?: number;
  tidalDirection?: number;
  positionAgeInMinutes: number;
}

export interface VesselDimensions {
  toBow: number;
  toStern: number;
  toStarboard: number;
  toPort: number;
}

export interface SpeedChartEntry {
  timestamp: number;
  speed?: number;
}

export interface ClockData {
  readonly time: string;
  readonly timezone: string;
  readonly dateString?: string;
}

export enum Mode {
  Liveview = 'Liveview',
  Playback = 'Playback',
}

export interface EcoRatingTooltip {
  isOpen: boolean;
  isDismissible: boolean;
  onHover: () => void;
  onHoverOut: () => void;
  onTap: () => void;
  onDismiss: () => void;
  onClose: () => void;
}

export interface TargetVessel extends VesselViewModel {
  isLockedOnTarget: boolean;
}

export interface MapData {
  liveData: LiveDataViewModel;
  vesselPositions: VesselPosition[];
  vesselData: Record<string, VesselViewModel>;
  isPlaybackDataLoading: boolean;
  isError: boolean;
  isLiveDataLoading: boolean;
}

export interface TimestampHandler {
  playbackTimestamp: ITimestamp;
  currentTimestamp: ITimestamp;
  handleSetPlaybackTimestamp: (timestamp: ITimestamp) => void;
}

export interface ModeSwitchHandler {
  switchToLiveview: () => void;
  switchToPlayback: () => void;
}

export interface TargetVesselHandler {
  targetVessel: TargetVessel;
  setTargetVesselMmsi: (mmsi: string) => void;
  setIsLockedOnTarget: (isLockedOnTarget: boolean) => void;
}
